import { Logger } from '@gitbook/util-debug';

export interface ConnectionPoolLifecycle<Connection> {
    /**
     * Number of milliseconds to keep a connection alive.
     */
    ttl: number;

    /**
     * Called when the connection is no longer needed.
     */
    onDestroy?: (connection: Connection) => void;
}

/**
 * How many time is the GC being run by ttl.
 * It needs to be higher than 1 to avoid timing conditions.
 */
const GC_FREQUENCY = 2;

const logger = Logger('ConnectionPool');

/**
 * Utilities to manage a pool of connections, keeping them open for a while.
 */
export class ConnectionPool<Connection extends object> {
    private connections: Map<
        string,
        {
            connection: Connection;
            /** Value of the GC clock, last time the sync was active (see `touch()` / `keepAlive()`) */
            clock: number;
        }
    > = new Map();

    private ids = new WeakMap<Connection, string>();

    private gcClock = 0;
    private gcInterval?: NodeJS.Timeout;

    constructor(private readonly lifecycle: ConnectionPoolLifecycle<Connection>) {}

    /**
     * Get a connection from the pool.
     */
    public get(id: string, initialize: () => Connection): Connection;
    public get(id: string): Connection | null;
    public get(id: string, initialize?: () => Connection): Connection | null {
        const existing = this.connections.get(id);
        if (existing) {
            return existing.connection;
        }

        if (initialize === undefined) {
            return null;
        }

        const connection = initialize();
        this.connections.set(id, { connection, clock: this.gcClock });
        this.ids.set(connection, id);

        this.startGarbageCollect();

        return connection;
    }

    /**
     * Get all open connections.
     */
    public getAll(): Connection[] {
        return [...this.connections.values()].map(({ connection }) => connection);
    }

    /**
     * Mark a connection as still active.
     */
    public touch(connection: Connection): boolean {
        const id = this.ids.get(connection);
        if (!id) {
            // Connection has been closed.
            return false;
        }

        const existing = this.connections.get(id);
        if (!existing) {
            throw new Error('Expected connection to be registered');
        }

        existing.clock = this.gcClock;

        return true;
    }

    /**
     * Keep a connection alive until the cleanup callback is called.
     * The connection is not closed when the cleanup callback is called, but can be garbage collected.
     */
    public keepAlive(connection: Connection): () => void {
        this.touch(connection);

        let interval: NodeJS.Timeout | undefined = setInterval(() => {
            if (!this.touch(connection)) {
                cancel();
            }
        }, this.lifecycle.ttl / 2);

        const cancel = () => {
            if (interval) {
                clearInterval(interval);
            }
            interval = undefined;
        };

        return cancel;
    }

    /**
     * Delete a connection from the pool.
     */
    public delete(connection: Connection) {
        const id = this.ids.get(connection);
        if (!id) {
            throw new Error('Unknown connection');
        }

        const existing = this.connections.get(id);
        if (!existing) {
            throw new Error('Expected connection to be registered');
        }

        this.lifecycle.onDestroy?.(existing.connection);
        this.connections.delete(id);
        this.ids.delete(connection);

        if (this.connections.size === 0) {
            this.stopGarbageCollect();
        }
    }

    /**
     * Delete all connections
     */
    public async deleteAll() {
        this.stopGarbageCollect();

        for (const { connection } of this.connections.values()) {
            this.lifecycle.onDestroy?.(connection);
        }

        this.connections = new Map();
        this.ids = new WeakMap();
    }

    /**
     * Garbage collect inactive sync clients.
     */
    private garbageCollect() {
        this.gcClock += 1;

        for (const [id, { connection, clock }] of this.connections.entries()) {
            if (clock < this.gcClock - GC_FREQUENCY) {
                const inactiveDuration =
                    (this.gcClock - clock) * (this.lifecycle.ttl / GC_FREQUENCY);

                logger.info(`garbage collection "${id}", last active ~${inactiveDuration}ms ago`);
                this.delete(connection);
            }
        }

        if (this.connections.size === 0) {
            this.stopGarbageCollect();
        }
    }

    /**
     * Start the garbage collection interval.
     */
    private startGarbageCollect() {
        if (!this.gcInterval) {
            this.gcInterval = setInterval(
                () => this.garbageCollect(),
                this.lifecycle.ttl / GC_FREQUENCY
            );
        }
    }

    /**
     * Stop the garbage collection interval.
     */
    private stopGarbageCollect() {
        if (this.gcInterval) {
            clearInterval(this.gcInterval);
            this.gcInterval = undefined;
        }
    }
}
